import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { gsap, ScrollTrigger } from 'gsap/all';
import { fullHeight, useHsHeader } from '../../util/hsHeader';
import { useDeviceSize } from '../../util/useDeviceSize';
import { AnimatedTextWrapper, centered } from '../../styles/helpers';
import { H3, P, Quote } from '../../styles/typography';
import VideoMobile from '../VideoMobile';
import FullScreenVideo from '../FullScreenVideo';
import MobileHorizontalGallery from '../MobileHorizontalGallery';

gsap.registerPlugin(ScrollTrigger);

export default function VideoSlide5({ images, copy, video }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [{ headerHeight }] = useHsHeader();
  const { isWideScreen } = useDeviceSize();

  const slideTriggerRef = useRef();
  const videoRef = useRef();
  const copyRef = useRef();
  const copy1Ref = useRef();
  const copy2Ref = useRef();
  const copy3Ref = useRef();
  const imageRef = useRef();
  const imageHolderRef = useRef();

  useEffect(() => {
    if (!isWideScreen) {
      return undefined;
    }

    const tl = gsap
      .timeline()
      .to(slideTriggerRef.current, {
        duration: 0.2,
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(slideTriggerRef.current, {
        duration: 0.4,
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(copyRef.current, {
        duration: 0.2,
        y: 0,
        ease: 'ease-in',
      })
      .to(copy2Ref.current, {
        duration: 0.2,
        x: 0,
        ease: 'ease-in',
      })
      .to(copy1Ref.current, {
        duration: 0.2,
        x: '-100%',
        ease: 'ease-in',
      })
      .to(videoRef.current, {
        duration: 0.2,
        opacity: 0,
        delay: -0.1,
        ease: 'ease-in',
      })
      .to(copy3Ref.current, {
        duration: 0.2,
        opacity: 1,
        delay: -0.1,
        ease: 'ease-in',
      })
      .to(copyRef.current, {
        duration: 0.2,
        delay: 0.1,
        opacity: 0,
        ease: 'ease-in',
      })
      .to(imageHolderRef.current, {
        duration: 0.4,
        delay: -0.1,
        y: '-100%',
        ease: 'ease-in',
      });

    const st = ScrollTrigger.create({
      animation: tl,
      trigger: slideTriggerRef.current,
      pin: true,
      pinSpacing: false,
      scrub: true,
      start: 'top top',
      end: `bottom 0`,
      anticipatePin: 1,
    });

    return () => {
      tl.kill();
      st.kill();
    };
  }, [headerHeight, isWideScreen]);

  if (isWideScreen) {
    return (
      <div
        css={css`
          ${fullHeight(7)};
          position: relative;
          z-index: 2;
          display: flex;
        `}
        ref={slideTriggerRef}
      >
        <div
          ref={videoRef}
          css={css`
            width: 100%;
          `}
        >
          <FullScreenVideo videoId={video.id} pause={!isVideoPlaying} />
        </div>

        <div
          css={css`
            ${fullHeight()};
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            pointer-events: none;
            transform: translateY(100vh);
            ${centered}
          `}
          ref={copyRef}
        >
          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              transform: translateY(-30%);
            `}
          >
            <P
              css={css`
                width: 100%;
                transform: translateX(100%);
                margin-bottom: 16px;
              `}
              ref={copy2Ref}
            >
              {copy[1].text}
            </P>
            <div
              css={css`
                width: 100%;
                position: relative;
                display: flex;
                ${centered}
              `}
            >
              <div
                ref={copy1Ref}
                css={css`
                  width: 100%;
                  display: flex;
                  ${centered}
                `}
              >
                <H3
                  css={css`
                    position: relative;
                    text-transform: uppercase;
                    max-width: 1200px;
                  `}
                >
                  {copy[0].text}
                </H3>
              </div>
              <H3
                css={css`
                  position: absolute;
                  top: 0;
                  width: 100%;
                  text-transform: uppercase;
                  opacity: 0;
                  max-width: 1200px;
                `}
                ref={copy3Ref}
              >
                {copy[2].text}
              </H3>
            </div>
          </div>
        </div>

        <div
          css={css`
            ${fullHeight()};
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            pointer-events: none;
            ${centered}
            transform: translateY(100%);
          `}
          ref={imageHolderRef}
        >
          <img
            css={css`
              height: 90vh;
              margin-right: 4px;
            `}
            src={images[1].src}
            alt=""
            ref={imageRef}
          />
          <img
            css={css`
              height: 90vh;
              margin-left: 4px;
            `}
            src={images[2].src}
            alt=""
            ref={imageRef}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <AnimatedTextWrapper>
        <Quote mobile>{copy[0].text}</Quote>
      </AnimatedTextWrapper>

      <VideoMobile videoId={video.id} />

      <AnimatedTextWrapper>
        <H3 mobile>{copy[1].text}</H3>
      </AnimatedTextWrapper>

      <AnimatedTextWrapper>
        <Quote mobile>{copy[2].text}</Quote>
      </AnimatedTextWrapper>

      <div
        css={css`
          height: calc(
            300vw + 32px * 2
          ); // TODO temporary fix to keep videos trigger at the right time. Figure out the solution
        `}
      >
        <MobileHorizontalGallery images={images} />
      </div>
    </div>
  );
}

VideoSlide5.propTypes = {
  video: PropTypes.shape({ id: PropTypes.number }),
  images: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
  copy: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.node })),
};

VideoSlide5.defaultProps = {
  video: PropTypes.shape({ id: PropTypes.number }),
  images: null,
  copy: null,
};
